import { Component, OnInit, ViewChild, TemplateRef, WritableSignal, signal, OnDestroy, effect, input, untracked } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ClientIntegrationService } from 'src/app/services/client-integration.service';
import { IntegrationRunTimeService } from 'src/app/services/integration-runtime.service';
import { IntegrationPushBackService } from 'src/app/services/integration-pushback.service';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { Integration } from '../../../../models/integration';
import { IntegrationPushBack } from '../../../../models/integration-pushback';
import { User } from '../../../../models/user';
import { ToastService } from '../../../../services/toast.service';
import { IntegrationRunTime } from 'src/app/models/integration-runtime';
import { IntegrationPushbackViewComponent } from '../integration-pushback-view/integration-pushback-view.component';
import { Subject, takeUntil } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { IntegrationPushbackAddComponent } from '../integration-pushback-add/integration-pushback-add.component';
import { CommonModule } from '@angular/common';
import { LTSpinnerComponent } from 'src/app/features/lt-shared-ui/lt-info/components/lt-spinner/lt-spinner.component';

@Component({
    selector: 'app-integration-pushback',
    templateUrl: './integration-pushback.component.html',
    styleUrls: ['./integration-pushback.component.scss'],
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        IntegrationPushbackViewComponent,
        IntegrationPushbackAddComponent,
        LTSpinnerComponent,
    ],
})
export class IntegrationPushBackComponent implements OnInit, OnDestroy {
    integration = input<Integration>();
    @ViewChild('deletePushBackDialog', { static: true }) deletePushBackDialog: TemplateRef<any>;
    @ViewChild('pushBackView') public pushBackView: IntegrationPushbackViewComponent;
    public gettingData: WritableSignal<boolean> = signal<boolean>(true);
    addingIntegrationPushBack = false;
    editingIntegrationPushBack = false;
    savingIntegrationPushBack = false;
    constructor(
        private clientService: ClientIntegrationService,
        private integrationPushBackService: IntegrationPushBackService,
        private integrationRunTimeService: IntegrationRunTimeService,
        private typeConstantService: TypeConstantService,
        private toastService: ToastService,
        private user: User,
        private dialog: MatDialog,
        private router: Router,
    ) {
        effect(() => {
            const integration = this.integration();
            if (integration) {
                untracked(() => this.loadForIntegration());
            }
        });
    }


    componentDestroyed$ = new Subject<boolean>();
    integrationRunTimes: IntegrationRunTime[];
    integrationPushBacks: IntegrationPushBack[];
    selectedPushBack: IntegrationPushBack; // for delete
    selectedRunTime: IntegrationRunTime;

    hasActivePushBacks(): boolean {
        return this.integrationPushBacks != null
            && this.integrationPushBacks.length > 0
            && this.integrationPushBacks.some(x => x.active == true);
    }

    ngOnInit(): void { }

    private loadForIntegration(): void {
        this.gettingData.set(true);
        this.integrationRunTimes = null;

        this.integrationRunTimeService.getAllIntegrationRunTimesByIntegration(this.integration().integrationId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
                next: (value) => {
                    // on success
                    this.integrationRunTimes = value.filter(r =>
                        !(r.destinationEndpointName == 'NoDestination')
                        && !r.importTypeDescription.includes('Cache'),
                    );

                    if (this.integrationRunTimes.length > 0) {
                        this.selectedRunTime = this.integrationRunTimes.find(runTime => runTime.importTypeDescription.toLowerCase().includes('loan'));
                        if (this.selectedRunTime == null) {
                            this.selectedRunTime = this.integrationRunTimes[0];
                        }

                        var integrationRunTimeIds = []
                        for (var r of this.integrationRunTimes) {
                            integrationRunTimeIds.push(r.integrationRunTimeId);
                        }
                        this.integrationPushBackService.getAllIntegrationPushBacksByRunTimes(integrationRunTimeIds).subscribe({
                            next: (pushBacks) => {
                                this.integrationPushBacks = pushBacks;
                            },
                            complete: () => {
                                this.gettingData.set(false);
                            },
                        })
                    }
                },
                error: (_error) => {
                    this.toastService.toastCreate('Failed to load integration run times for ' + this.integration().name, 'Danger', {
                        autoClose: false,
                    });
                    this.gettingData.set(false);
                },
            });
    }


    ngOnDestroy() {
        this.componentDestroyed$.next(true);
        this.componentDestroyed$.complete();
    }

    //#region - Add Pushback
    integrationPushBackAdded(event: any): void {
        this.integrationPushBacks.unshift(event);
        this.addingIntegrationPushBack = false;
        this.savingIntegrationPushBack = false;
    }

    integrationPushBackUpdated(event: any): void {
        this.integrationPushBacks = this.integrationPushBacks.filter(pushBack => !pushBack.active)
        this.integrationPushBacks.unshift(event);
        this.editingIntegrationPushBack = false;
        this.savingIntegrationPushBack = false;
    }

    integrationPushBackCancelAdded(_event: any): void {
        this.addingIntegrationPushBack = false;
        this.editingIntegrationPushBack = false;
        this.savingIntegrationPushBack = false;
    }

    setAddingIntegrationPushBack(): void {
        this.selectedPushBack = null;
        this.addingIntegrationPushBack = true;
    }

    //#endregion

    checkAccess(role: string[]): boolean {
        //User requires DevOps.Write or Admin
        return this.user.checkAccess(role);
    }

    setEditingIntegrationRunTime(integrationPushBack: IntegrationPushBack = null): void {
        if (integrationPushBack != null) {
            this.selectedPushBack = integrationPushBack;
            this.editingIntegrationPushBack = true;
        }
    }

    //#region - Delete Pushback
    setDeletingIntegrationRunTime(integrationPushBack: IntegrationPushBack = null): void {
        if (integrationPushBack != null) {
            this.selectedPushBack = integrationPushBack;
            this.dialog.open(this.deletePushBackDialog);
        }
    }

    deleteIntegrationPushBack(): void {
        this.savingIntegrationPushBack = true;
        this.integrationPushBackService.deleteActiveIntegrationPushBack(this.selectedPushBack.integrationRunTimeId).subscribe({
            next: () => {
                this.integrationPushBacks = this.integrationPushBacks.filter(pushback => pushback.integrationPushBackId != this.selectedPushBack.integrationPushBackId);
                this.toastService.toastCreate('Successfully Deleted Active PushBack for ' + this.integration().name + ' runtime: ' + this.selectedRunTime.importTypeDescription + ' ' + this.selectedRunTime.destinationEndpointName + '.', 'Success', {
                    autoClose: false,
                });
            },
            error: (_error) => {
                this.toastService.toastCreate('Failed to delete active pushback for ' + this.integration().name + ' runtime: ' + this.selectedRunTime.importTypeDescription + ' ' + this.selectedRunTime.destinationEndpointName + '.', 'Danger', {
                    autoClose: false,   
                });
            },
            complete: () => {
                this.dialog.closeAll();
                this.savingIntegrationPushBack = false;
            }
        });
    }
    //#endregion
}
